import React, { useState, useCallback, useEffect } from 'react';
import { TemplateElement } from '@agency-smm/shared';

type EditMode = 'pan' | 'elements';

export function useElementEditing(
  editMode: EditMode,
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
) {
  const [modifiedElements, setModifiedElements] = useState<TemplateElement[]>([]);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [hasElementChanges, setHasElementChanges] = useState(false);
  const [elementDragStart, setElementDragStart] = useState<{ x: number; y: number; elementX: number; elementY: number } | null>(null);
  const [resizeStart, setResizeStart] = useState<{ x: number; y: number; width: number; height: number; handle: string } | null>(null);
  // Rotation drag state — captures element center in canvas pixel coords +
  // initial mouse angle + element's rotation when drag started.
  const [rotateStart, setRotateStart] = useState<{
    centerX: number;
    centerY: number;
    initialAngle: number;
    initialRotation: number;
  } | null>(null);

  const handleElementMouseDown = useCallback((e: React.MouseEvent, element: TemplateElement) => {
    if (editMode !== 'elements') return;
    e.preventDefault();
    e.stopPropagation();

    setSelectedElementId(element.id);
    setElementDragStart({
      x: e.clientX,
      y: e.clientY,
      elementX: element.transform.positionX,
      elementY: element.transform.positionY,
    });
  }, [editMode]);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent, element: TemplateElement, handle: string) => {
    if (editMode !== 'elements') return;
    e.preventDefault();
    e.stopPropagation();

    setSelectedElementId(element.id);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: element.transform.width,
      height: element.transform.height,
      handle,
    });
  }, [editMode]);

  const handleRotateMouseDown = useCallback((e: React.MouseEvent, element: TemplateElement) => {
    if (editMode !== 'elements') return;
    e.preventDefault();
    e.stopPropagation();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    // Element center in viewport pixels
    const centerX = rect.left + (element.transform.positionX / 100) * rect.width;
    const centerY = rect.top + (element.transform.positionY / 100) * rect.height;
    // Initial angle from element center to current mouse position (degrees)
    const initialAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * 180 / Math.PI;
    setSelectedElementId(element.id);
    setRotateStart({
      centerX,
      centerY,
      initialAngle,
      initialRotation: element.transform.rotation || 0,
    });
  }, [editMode, canvasRef]);

  const handleElementMouseMove = useCallback((e: React.MouseEvent) => {
    if (editMode !== 'elements' || !selectedElementId) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const canvasWidth = rect.width;
    const canvasHeight = rect.height;

    if (elementDragStart) {
      const deltaX = ((e.clientX - elementDragStart.x) / canvasWidth) * 100;
      const deltaY = ((e.clientY - elementDragStart.y) / canvasHeight) * 100;

      const newX = Math.max(0, Math.min(100, elementDragStart.elementX + deltaX));
      const newY = Math.max(0, Math.min(100, elementDragStart.elementY + deltaY));

      setModifiedElements((prev) =>
        prev.map((el) =>
          el.id === selectedElementId
            ? { ...el, transform: { ...el.transform, positionX: newX, positionY: newY } }
            : el
        )
      );
      setHasElementChanges(true);
    } else if (resizeStart) {
      const deltaX = ((e.clientX - resizeStart.x) / canvasWidth) * 100;
      const deltaY = ((e.clientY - resizeStart.y) / canvasHeight) * 100;

      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;

      if (resizeStart.handle.includes('e')) newWidth = Math.max(5, resizeStart.width + deltaX);
      if (resizeStart.handle.includes('w')) newWidth = Math.max(5, resizeStart.width - deltaX);
      if (resizeStart.handle.includes('s')) newHeight = Math.max(5, resizeStart.height + deltaY);
      if (resizeStart.handle.includes('n')) newHeight = Math.max(5, resizeStart.height - deltaY);

      setModifiedElements((prev) =>
        prev.map((el) =>
          el.id === selectedElementId
            ? { ...el, transform: { ...el.transform, width: newWidth, height: newHeight } }
            : el
        )
      );
      setHasElementChanges(true);
    } else if (rotateStart) {
      // Current mouse angle from element center
      const currentAngle = Math.atan2(
        e.clientY - rotateStart.centerY,
        e.clientX - rotateStart.centerX
      ) * 180 / Math.PI;
      let newRotation = rotateStart.initialRotation + (currentAngle - rotateStart.initialAngle);
      // Snap to multiples of 15° when shift is held
      if (e.shiftKey) {
        newRotation = Math.round(newRotation / 15) * 15;
      } else {
        newRotation = Math.round(newRotation);
      }
      // Normalize to -180..180 range
      while (newRotation > 180) newRotation -= 360;
      while (newRotation < -180) newRotation += 360;
      setModifiedElements((prev) =>
        prev.map((el) =>
          el.id === selectedElementId
            ? { ...el, transform: { ...el.transform, rotation: newRotation } }
            : el
        )
      );
      setHasElementChanges(true);
    }
  }, [editMode, selectedElementId, elementDragStart, resizeStart, rotateStart, canvasRef]);

  const handleElementMouseUp = useCallback(() => {
    setElementDragStart(null);
    setResizeStart(null);
    setRotateStart(null);
  }, []);

  // Global mouse up handler for element editing
  useEffect(() => {
    if (!elementDragStart && !resizeStart && !rotateStart) return;
    const handleGlobalUp = () => handleElementMouseUp();
    window.addEventListener('mouseup', handleGlobalUp);
    window.addEventListener('touchend', handleGlobalUp);
    return () => {
      window.removeEventListener('mouseup', handleGlobalUp);
      window.removeEventListener('touchend', handleGlobalUp);
    };
  }, [elementDragStart, resizeStart, rotateStart, handleElementMouseUp]);

  const resetElementEditing = useCallback(() => {
    setSelectedElementId(null);
    setHasElementChanges(false);
  }, []);

  return {
    modifiedElements,
    setModifiedElements,
    selectedElementId,
    setSelectedElementId,
    hasElementChanges,
    setHasElementChanges,
    handleElementMouseDown,
    handleResizeMouseDown,
    handleRotateMouseDown,
    handleElementMouseMove,
    handleElementMouseUp,
    resetElementEditing,
  };
}
